import {
  QueryClient,
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult
} from '@tanstack/react-query';
import { TenantSettingsDto } from '../../../shared/dtos/tenant.dto';
import { useAuthFetchFromRestAPI } from '../util/api';
import { AUTH0_ORGANIZATION_ID } from '../config';
import { TenantSettingsRequest } from '../../../shared/requests/tenantSettings.request';

export const tenantSettingsQueryKeys = {
  all: ['tenant-settings'] as const,
  detail: () => [...tenantSettingsQueryKeys.all, 'detail'] as const,
}

export function useTenantSettings(enabled: boolean = true): UseQueryResult<TenantSettingsDto> {
  const { authFetch } = useAuthFetchFromRestAPI();
  
  return useQuery({
    queryKey: tenantSettingsQueryKeys.detail(),
    queryFn: async (): Promise<TenantSettingsDto> => {
      try {
        return await authFetch(`/api/v1/tenant/${AUTH0_ORGANIZATION_ID}/settings`)
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    enabled,
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 30,
  })
}


export function useUpdateTenantSettings(): UseMutationResult<TenantSettingsDto, Error, TenantSettingsRequest> {
  const { authFetch } = useAuthFetchFromRestAPI();
  const queryClient: QueryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (settings: TenantSettingsRequest): Promise<TenantSettingsDto> => {
      try {
        return await authFetch(`/api/v1/tenant/${AUTH0_ORGANIZATION_ID}/settings`, {
          method: 'PUT',
          body: settings,
        })
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    onSuccess: (updated: TenantSettingsDto) => {
      queryClient.setQueryData(
        tenantSettingsQueryKeys.detail(),
        updated,
      )
    }
  })
}

export function useIsVideoFeedbackEnabled(): boolean {
  const { data } = useTenantSettings();
  
  return data?.isVideoFeedbackEnabled ?? false;
}

export function useMaxTrainingCount(): number {
  const { data } = useTenantSettings();

  return data?.maxTrainingCount ?? 12;
}